import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORY_COLORS, formatSeconds, competitionResultLabel } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { Calendar, MapPin, Trophy, Users, ArrowRight, Sparkles } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

function DateBadge({ dateStr }) {
  if (!dateStr) return null;
  let d;
  try { d = new Date(dateStr + "T00:00:00"); } catch { return null; }
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const year = d.getFullYear();
  return (
    <div className="shrink-0 w-24 rounded-2xl bg-white border-2 border-[#FF9F1C] shadow-[0_8px_20px_rgb(255,159,28,0.15)] overflow-hidden text-center" data-testid="past-event-date-badge">
      <div className="bg-[#FF9F1C] text-white text-[11px] font-black uppercase tracking-widest py-1.5">{month}</div>
      <div className="font-display font-black text-4xl leading-none pt-2 text-[#1E1E24]">{day}</div>
      <div className="text-[10px] text-[#7A7D8C] pb-2 pt-1 font-semibold">{year}</div>
    </div>
  );
}

const MEDAL = ["🥇", "🥈", "🥉"];

export default function PastEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events/past")
      .then((r) => setEvents(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const competitions = events.filter((e) => e.kind === "competition");
  const camps = events.filter((e) => e.kind === "camp");

  return (
    <div>
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Archive</span>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">Past events & winners.</h1>
        <p className="mt-6 max-w-2xl text-lg text-[#7A7D8C]">Look back at previous Naptown Cubers competitions and camps. Every personal best deserves a shout out.</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24 space-y-16">
        {loading && (
          <div className="rounded-3xl border border-slate-100 bg-white p-16 text-center text-[#7A7D8C]" data-testid="past-events-loading">Loading past events…</div>
        )}

        {!loading && events.length === 0 && (
          <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-16 text-center" data-testid="past-events-empty">
            <Sparkles className="w-8 h-8 text-[#FF9F1C] mx-auto" />
            <h2 className="mt-4 font-display font-black text-2xl">No past events yet</h2>
            <p className="mt-2 text-[#7A7D8C]">Our first event is coming — check the <Link to="/competitions" className="text-[#FF9F1C] font-semibold">Competitions</Link> page.</p>
          </div>
        )}

        {!loading && competitions.length > 0 && (
          <div>
            <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2 mb-6"><Trophy className="w-5 h-5 text-[#FF9F1C]" /> Competition results</h2>
            <div className="grid gap-6">
              {competitions.map((e) => <CompetitionCard key={e.id} event={e} />)}
            </div>
          </div>
        )}

        {!loading && camps.length > 0 && (
          <div>
            <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2 mb-6"><Calendar className="w-5 h-5 text-[#FF9F1C]" /> Past camps</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {camps.map((e) => <CampCard key={e.id} event={e} />)}
            </div>
          </div>
        )}

        <div className="rounded-[36px] bg-[#FFBF69]/15 border border-[#FFBF69]/40 p-10 md:p-14 text-center">
          <h3 className="font-display font-black text-3xl">Ready to run another one?</h3>
          <p className="mt-3 text-[#7A7D8C] max-w-xl mx-auto">Head back to your events roster or set up the next competition.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/admin/events" data-testid="archive-cta-events" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold hover:-translate-y-0.5 transition-transform shadow-[0_12px_30px_rgb(255,159,28,0.35)]">Manage events <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/admin" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 font-bold hover:border-[#FF9F1C] hover:text-[#FF9F1C]">Dashboard</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function CompetitionCard({ event }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const rankings = event.rankings || {};
  const anyResults = ALL_CATEGORIES.some((c) => (rankings[c] || []).length > 0);
  return (
    <article className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`past-event-${event.id}`}>
      <div className="flex flex-wrap gap-5 items-start">
        <DateBadge dateStr={event.start_date} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[#FF9F1C] font-black">Competition</div>
          <h3 className="mt-1 font-display font-black text-2xl tracking-tight">{event.title}</h3>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-[#7A7D8C]">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(event.start_date)}{event.end_date && event.end_date !== event.start_date ? ` – ${fmtDate(event.end_date)}` : ""}</span>
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
            {event.competitor_count != null && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {event.competitor_count} competitor{event.competitor_count === 1 ? "" : "s"}</span>}
          </div>
        </div>
      </div>

      {!anyResults ? (
        <p className="mt-6 text-sm text-[#7A7D8C]">Results not published for this event.</p>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {ALL_CATEGORIES.map((cat) => {
            const rows = (rankings[cat] || []).filter((r) => !r.official_dnf && r.official_average != null);
            return (
              <div key={cat} className="rounded-2xl border border-slate-100 bg-[#FDFFFC] p-5" data-testid={`past-event-${event.id}-cat-${cat}`}>
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CATEGORY_COLORS[cat]}`}>{CATEGORY_LABEL[cat]}</span>
                {rows.length === 0 ? (
                  <p className="mt-3 text-xs text-[#7A7D8C]">No official finishers in this category.</p>
                ) : (
                  <ol className="mt-3 space-y-2">
                    {rows.slice(0, 3).map((r, i) => (
                      <li key={r.id} data-testid={`past-result-${r.id}`} className="flex items-center gap-3 text-sm">
                        <span className="text-lg" aria-hidden>{MEDAL[i] || "•"}</span>
                        <span className="font-semibold flex-1 truncate">{r.full_name}</span>
                        <span className="text-[#7A7D8C] text-xs text-right" data-testid={`past-result-time-${r.id}`}><span className="block font-mono">{formatSeconds(r.official_average)}</span><span className="block text-[10px]">{competitionResultLabel(r)}</span></span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
}

function CampCard({ event }) {
  return (
    <article className="rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid={`past-camp-${event.id}`}>
      <div className="flex gap-4 items-start">
        <DateBadge dateStr={event.start_date} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[#B36906] font-black">Camp</div>
          <h3 className="mt-1 font-display font-black text-xl tracking-tight">{event.title}</h3>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#7A7D8C]">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(event.start_date)}{event.end_date && event.end_date !== event.start_date ? ` – ${fmtDate(event.end_date)}` : ""}</span>
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
          </div>
          {event.description && <p className="mt-3 text-sm text-[#1E1E24]/90 whitespace-pre-wrap">{event.description}</p>}
        </div>
      </div>
    </article>
  );
}
